<template>
  <LocalFileUploaderModal
    hideHeader
    noCloseOnBackdrop
    :modalSize="'5xl'"
    :modalClass="'upload-image-modal'"
    :isModalVisible="modalVisible"
    :staticState="!isUploadingLocalImage"
    :uploadingState="isUploadingLocalImage && selectedTab === ActiveTabNames.UPLOAD"
    :uploadError="$store.state.uploadLocalImageError"
    :featureName="UploadFeatureName.UPLOAD_IMAGE"
    :instructionText="instructionText"
    :fileFormat="'image/*'"
    :dropPlaceholder="'drop image'"
    :icon="uploadImageIcon"
    :selectedTab="selectedTab"
    :signedCFUrlExpiryDate="signedCFUrlExpiryDate"
    :featureToUploadImage="type"
    @hide="hide()"
  >
    <template #headerSection>
      <div class="relative">
        <div class="modal__title--tab center">
          <a
            v-if="tabs.includes(ActiveTabNames.UPLOAD)"
            @click="handleTabChange(ActiveTabNames.UPLOAD)"
            :class="{ active: selectedTab === ActiveTabNames.UPLOAD }"
          >
            {{ $i18nPresenter.t(ActiveTabNames.UPLOAD) }}
          </a>
          <a
            v-if="tabs.includes(ActiveTabNames.MY_IMAGES)"
            @click="handleTabChange(ActiveTabNames.MY_IMAGES)"
            :class="{ active: selectedTab === ActiveTabNames.MY_IMAGES }"
          >
            {{ $i18nPresenter.t(ActiveTabNames.MY_IMAGES) }}
          </a>
          <a
            v-if="tabs.includes(ActiveTabNames.UNSPLASH)"
            @click="handleTabChange(ActiveTabNames.UNSPLASH)"
            :class="{ active: selectedTab === ActiveTabNames.UNSPLASH }"
          >
            <span class="hidden xl:block lg:block md:block">{{ $i18nPresenter.t('Stock Photos') }}</span>
            <span class="block md:hidden">{{ $i18nPresenter.t('Photos') }}</span>
          </a>
          <a
            v-if="tabs.includes(ActiveTabNames.GIPHY)"
            @click="handleTabChange(ActiveTabNames.GIPHY)"
            :class="{ active: selectedTab === ActiveTabNames.GIPHY }"
            class="tab-giphy __wrapper"
          >
            <span class="hidden xl:block lg:block md:block">{{ $i18nPresenter.t('GIFs & Stickers') }}</span>
            <span class="block md:hidden">{{ $i18nPresenter.t('GIFs') }}</span>
            <span :class="{'hidden': hasUsedGiphy}" class="tab-giphy __icon">
              <img alt="giphy-star-icon" :src="require('@/assets/images/icon/star.gif')">
            </span>
          </a>
        </div>
        <aha-icon
          name="close"
          height="20"
          width="20"
          viewBox="0 0 24 24"
          @click="hide()"
          class="cursor-pointer text-base-70 hover:text-primary-90 absolute -top-2 right-4"
        />
      </div>
    </template>
    <template #secondTabContentSection>
      <MyPhoto
        v-show="selectedTab === ActiveTabNames.MY_IMAGES"
        @selectExistImage="(obj) => $emit('selectExistImage', obj)"
        @changeUploadTab="handleTabChange(ActiveTabNames.UPLOAD)"
      />
      <StockPhoto
        v-show="selectedTab === ActiveTabNames.UNSPLASH"
        @searchPhotoUnsplash="searchPhotoUnsplash"
        :splashPhotos="splashPhotos"
        :isLoadingImg="isLoadingImg"
        :noResultText="noResultText"
        @selectImage="selectUnsplashImage"
        :text="text"
        @update:text="updateText"
      />
      <!-- GIPHY -->
      <div
        class="tab-content list-giphy-container"
        v-show="selectedTab === ActiveTabNames.GIPHY"
        id="infinite-giphy-list"
      >
        <div class="search-giphy-group">
          <div class="input-search w-full">
            <b-form-input
              v-model="text"
              :placeholder="getBFormSearchGiphyPlaceHolder"
              @input="searchPhotoGiphy"
              ref="giphy-search-input"
              class="w-full"
              name="'GifsStickersSearchInput'"
              v-aha-emit-action
            />
          </div>
          <div class="btn-giphy--type">
            <div
              v-if="giphyTypes.includes('gif')"
              @click="handleGiphyTypeChange('gif')"
              class="item gif"
              :class="{ active: giphyType === 'gif' }"
            >
              <span>GIFs</span>
            </div>
            <div
              v-if="giphyTypes.includes('sticker')"
              @click="handleGiphyTypeChange('sticker')"
              class="item sticker"
              :class="{ active: giphyType === 'sticker' }"
            >
              <span>{{ $i18nPresenter.t('Stickers') }}</span>
            </div>
          </div>
        </div>
        <div class="list-image">
          <p v-if="noResultText" class="list-image--no-result">
            {{ noResultText }}
          </p>
          <div class="pt-0 item-image" v-for="(item, index) in giphyPhotos" :key="index">
            <div class="item-image-group text-center">
              <div v-if="giphyType === 'sticker'" class="bg-sticker"></div>
              <img
                class="img-bg inline-block"
                :src="getGifPreviewUrl(item, giphyType)"
                :class="{
                  'object-contain': giphyType === 'sticker',
                  'object-cover w-full': giphyType === 'gif',
                }"
                @click="selectImage(
                  getGiphyImageUrl(item), giphyType === 'gif' ? 'giphy' : 'giphy stickers', item)"
                @load="handleImageLoad"
                @error="handleImageError"
              />
            </div>
          </div>
        </div>
        <Loading
          style="margin: 0 auto; width: 100%;"
          :class="{'hide-loading-image': !isLoadingImg}"
        />
        <ModalUploadImageError
          v-if="hasErrorOnUploadingGiphy"
          @handleOkButtonClick="handleOKButtonOnModalUploadImageError()"
        />
      </div>
    </template>
  </LocalFileUploaderModal>
</template>

<script>
import debounce from 'debounce'
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import constants from '@/constant'
import Config from '@/config'
import Utils from '@/util'
import storageKeys from '@/constant/storageKeys'
import UserApi from '@/api/user'
import { dataURLToBlob } from '@/utils/indexedDB/image.cache.utils'
import eventCategories from '@/constant/eventCategories'
import SlideApi from '@/api/slide'
import EventBus from '@/eventBus'
import UploadFeatureName from '@/constant/uploadFeatureName'
import ActiveTabNames from '@/constant/ActiveTabNames'

// components
import LocalFileUploaderModal from '@/components-v2/modals/LocalFileUploaderModal.vue'

import Loading from '@/components/Loading'
// import Uploader from '@/components/UI/loaders/Uploader'
import ModalUploadImageError from '@/components/UI/modals/UploadImageError.vue'
import PasteImageMixin from '@/mixins/presentation/PasteImageMixin'
import MyPhoto from '@/components/presentations/MyPhoto.vue'
import StockPhoto from '@/components/presentations/StockPhoto.vue'
import TenorAPI from '@/api/tenor'

export default {
  components: {
    Loading,
    // Uploader,
    ModalUploadImageError,
    MyPhoto,
    StockPhoto,
    LocalFileUploaderModal,
  },
  mixins: [PasteImageMixin],
  props: {
    type: {
      type: String,
      default: null,
    },
    signedCFUrlExpiryDate: { // expiration of signed url by CloudFront
      type: Number,
      default: null,
    },
    updateGifInBackground: {
      type: Function,
      default: null,
    },
    giphyTypes: {
      type: Array,
      default: () => ['gif', 'sticker'],
    },
    tabs: {
      type: Array,
      default: () => [ActiveTabNames.UPLOAD, ActiveTabNames.MY_IMAGES, ActiveTabNames.UNSPLASH, ActiveTabNames.GIPHY],
    },
  },
  data() {
    return {
      text: '',
      giphyPhotos: [],
      splashPhotos: [],
      file: null,
      modalVisible: false,
      isLoadingImg: false,
      previousSearchText: '',
      noResultText: '',
      noMoreGiphyResult: false,
      noMoreUnsplashResult: false,
      hasErrorOnUploadingGiphy: false,
      giphyType: 'sticker',
      isHasUsedGiphyAPIDone: false,
      UploadFeatureName,
      ActiveTabNames,
      gifNextPos: null,
      gifLoadErrorCount: 0,
      processedGifImageCount: 0,
      totalGifImageCount: 0,
    }
  },
  computed: {
    ...mapGetters(['selectedSlides']),

    ...mapState({
      selectedTab: (state) => state.selections.selectedTab,
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      isUploadingLocalImage: (state) => state.isUploadingLocalImage,
    }),
    hasUsedGiphy() {
      return this.currentUser && this.currentUser.personalInformation &&
        this.currentUser.personalInformation.hasUsedGiphy
    },
    getBFormSearchGiphyPlaceHolder() {
      if (this.giphyType === 'gif') return this.$i18nPresenter.t('Search GIFs from TENOR...')
      return this.$i18nPresenter.t('Search Stickers from TENOR...')
    },
    instructionText() {
      return this.$i18nPresenter.t('PNG, JPEG, GIF, SVG - Max: 15MB')
    },
    uploadImageIcon() {
      return {
        name: 'empty-photo',
        width: 72,
        height: 56,
        viewBox: '0 0 72 56',
      }
    },
  },

  mounted() {
    this.show()
    this._resetAllUploadImageState()
    this.handleTabChange(this.selectedTab, true) // default tab: 'upload'

    EventBus.$on('Upload Image V2: updateGifInBackground function', async (payload) => {
      await this.updateGifInBackground(payload)
    })
    EventBus.$on('Upload Image V2: callbackUploadImageV2', (fileLocation, type, file) => {
      this.$emit('callback', fileLocation, type, file)
    })
    EventBus.$on('Upload Image V2: trackUploadImage', (type) => {
      this.trackUploadImage(type)
    })
    setTimeout(() => {
      this._setUnslashGiphyInfiniteScroll()
    }, 1000)

    setTimeout(() => {
      // Prevent deselect
      const modal = document.getElementById('upload-image-modal')
      if (!modal) return
      modal.addEventListener('mouseup', (e) => {
        e.stopPropagation()
      }, true)
    }, 0)
  },
  destroyed() {
    EventBus.$off('Upload Image V2: updateGifInBackground function')
    EventBus.$off('Upload Image V2: callbackUploadImageV2')
    EventBus.$off('Upload Image V2: trackUploadImage')
  },
  methods: {
    ...mapActions(['setSelectedTabAction', 'updateSlideActiveAction', 'setCurrentUserAction']),

    _resetAllUploadImageState() {
      this.$store.state.uploadLocalImageError = null
    },

    _checkToUpdateUserHasUsedGiphy() {
      const key = storageKeys.HAS_USED_GIPHY
      const timeUsedGiphy = localStorage.getItem(key)
      if (!timeUsedGiphy) {
        localStorage.setItem(key, 1)
      } else {
        localStorage.removeItem(key)
        this._callAPIToUpdateHasUsedGiphy()
      }
    },
    async _callAPIToUpdateHasUsedGiphy() {
      try {
        await UserApi.patchUserById(this.currentUser.id, {
          personalInformation: {
            hasUsedGiphy: true,
          },
        })
        this.isHasUsedGiphyAPIDone = true
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    _setUnslashGiphyInfiniteScroll() {
      const listElm = document.querySelector('#infinite-list')
      const listGiphy = document.querySelector('#infinite-giphy-list')
      if (listElm) {
        listElm.addEventListener('scroll', () => {
          if (this.noMoreUnsplashResult) return
          if (listElm.scrollTop + listElm.clientHeight >= listElm.scrollHeight - 50) {
            this.unsplashInfinite()
          }
        })
      }

      if (listGiphy) {
        listGiphy.addEventListener('scroll', () => {
          if (this.noMoreGiphyResult) return
          if (listGiphy.scrollTop + listGiphy.clientHeight >= listGiphy.scrollHeight - 50) {
            this.giphyInfinite()
          }
        })
      }
    },
    async resizeImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = function handleReaderLoad(readerEvent) {
          const image = new Image()
          image.onload = function handleReaderOnload() {
          // Resize the image
            const canvas = document.createElement('canvas')
            const max_size = 1024
            let { width, height } = image

            if (width > height) {
              if (width > max_size) {
                height *= max_size / width
                width = max_size
              }
            } else if (height > max_size) {
              width *= max_size / height
              height = max_size
            }
            canvas.width = width
            canvas.height = height
            canvas.getContext('2d', { alpha: true }).drawImage(image, 0, 0, width, height)
            const dataUrl = canvas.toDataURL(file.type)
            const resizedImage = dataURLToBlob(dataUrl)
            const resizedFile = new File([resizedImage], file.name, {
              type: file.type,
            })
            resolve(resizedFile)
          }
          image.src = readerEvent.target.result
        }
        reader.readAsDataURL(file)
      })
    },
    async selectImage(url, source, image) {
      this.hasErrorOnUploadingGiphy = false
      this.$store.state.isUploadingLocalImage = true

      const extension = url.split('?')[0].split('.').pop()

      if (extension === 'gif') {
        try {
          const imageUrl = this.getGiphyImageUrl(image)
          const mediaFormat = this.giphyType === 'sticker' ? 'gif_transparent' : 'gif'
          const imageDimension = {
            width: image.media_formats[mediaFormat].dims[0],
            height: image.media_formats[mediaFormat].dims[1],
          }
          await this.updateGifInBackground({
            imageUrl,
            selectedSlides: this.selectedSlides,
            imageDimension,
          })
          this.trackUploadImage(source)
        } catch (error) {
          Sentry.captureException(error)
          this.hasErrorOnUploadingGiphy = true
          this.$store.state.isUploadingLocalImage = false
        }
        return
      }

      this.$emit('callback', url, source)
      this.trackUploadImage(source)
    },

    show() {
      this.$store.state.isUploadingLocalImage = false
      this.modalVisible = true
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Open Upload modal', null, null, {
        slideId: this.slideActive.id,
      })
    },
    hide() {
      this.$store.state.isUploadingLocalImage = false
      this.modalVisible = false
      this.$emit('hideModal')
      if (this.isHasUsedGiphyAPIDone) {
        const userPayload = {
          personalInformation: {
            hasUsedGiphy: true,
          },
        }
        this.setCurrentUserAction({
          ...this.currentUser,
          ...userPayload,
        })
      }
    },
    trackUploadImage(source) {
      Utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_EDITOR,
        'Upload image',
        this.type,
        source,
        {
          slideId: this.slideActive.id,
        },
      )
    },
    // implement PasteImageMixin@uploadImgFromPaste
    uploadImgFromPaste(file) {
      if (this.selectedTab !== this.ActiveTabNames.UPLOAD) return
      EventBus.$emit('LocalFileUploader: uploadFile', [file])
      this.trackUploadImage('paste')
    },
    handleTabChange(selectedTab, previouslyCached = false) {
      this.setSelectedTabAction(selectedTab)
      this.noResultText = ''
      if (selectedTab === this.ActiveTabNames.UNSPLASH) {
        this.searchPhotoUnsplash()
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Free Photo (Unsplash)',
        )
        return
      }

      if (selectedTab === this.ActiveTabNames.GIPHY) {
        this.gifNextPos = null
        this.focusGifSearchInput()
        this.searchPhotoGiphy()
        if (!this.hasUsedGiphy) this._checkToUpdateUserHasUsedGiphy()
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Giphy',
        )
      }

      if (selectedTab === this.ActiveTabNames.MY_IMAGES) {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open My photo',
          previouslyCached ? 'Cached' : 'Change Tab',
        )
      }

      if (selectedTab === this.ActiveTabNames.UPLOAD) {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Open Upload',
        )
      }
    },
    // eslint-disable-next-line func-names
    searchPhotoUnsplash: debounce(function (infinitePage, e) {
      if (this.previousSearchText !== this.text) {
        this.noMoreUnsplashResult = false
      }
      this.noResultText = ''
      let text = this.text || 'team'

      let page = 1
      if (infinitePage) {
        page = infinitePage
      }
      this.isLoadingImg = true
      text = encodeURIComponent(text)
      const payload = {
        text,
        page,
        imagesPerPage: 30,
      }
      this._handleSearchUnsplash(text, page, e, payload)
    }, 800),

    async _handleSearchUnsplash(text, page, e, payload) {
      const listElm = document.querySelector('#infinite-list')
      try {
        const res = await this.$http.post(Config.api.searchUnsplash, payload)
        if (text && res.body.total === 0) {
          this.noResultText = `We couldn't find any images matching "${this.text}" in Unsplash.`
        }
        if (text && page > res.body.total_pages) {
          this.noMoreUnsplashResult = true // To stop calling searchUnplash API when scroll to bottom
          this.isLoadingImg = false
          return
        }
        if (!e) {
          this.splashPhotos = res.body.results
        } else {
          this.splashPhotos.forEach((splash) => {
            res.body.results = res.body.results.filter((result) => {
              return result.id !== splash.id
            })
          })
          this.splashPhotos.push(...res.body.results)
        }
        if (page === 1 && listElm) {
          listElm.scrollTop = 0
        }
        this.previousSearchText = this.text
        this.isLoadingImg = false
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Unsplash > Search',
        )
        setTimeout(() => {
          if (!this.$refs['unsplash-search-input']) return
          this.$refs['unsplash-search-input'].focus()
        })
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Search images failed. Please try again.'))
      }
    },

    unsplashInfinite() {
      this.noMoreGiphyResult = false
      const totalPagesNow = Math.round(this.splashPhotos.length / 30)
      const infinitePage = totalPagesNow + 1
      this.searchPhotoUnsplash(infinitePage, 'nextPage')
    },

    // eslint-disable-next-line func-names
    searchPhotoGiphy: debounce(function () {
      this.noResultText = ''
      this.isLoadingImg = true
      const text = this.text || 'team'

      if (this.previousSearchText !== text) {
        this.noMoreGiphyResult = false
        this.gifNextPos = null
      }

      const payload = {
        q: text,
        limit: 30,
        media_filter: 'gif,gif_transparent,webp,webp_transparent',
      }

      if (this.gifNextPos) {
        payload.pos = this.gifNextPos
      }

      if (this.giphyType === 'sticker') {
        payload.searchfilter = 'sticker'
      }

      this._handleSearchGiphy(payload)
    }, 1200),

    async _handleSearchGiphy(payload) {
      try {
        const { results, next } = await TenorAPI.search(payload)
        this.gifNextPos = next

        if (payload.q && !results.length) {
          if (!next) {
            this.noMoreGiphyResult = true // To stop calling searchGiphy API when scroll to bottom
            return
          }
          this.noResultText = `We couldn't find any images matching "${payload.q}" in Tenor.`
        }

        this.totalGifImageCount = results && results.length

        if (this.previousSearchText !== payload.q) {
          this.giphyPhotos = results
        } else {
          this.giphyPhotos.push(...results)
        }

        this.previousSearchText = payload.q
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          this.giphyType === 'gif' ? 'Giphy > Search Gif' : 'Giphy > Search Sticker',
        )
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Search images failed. Please try again.'))
      } finally {
        this.isLoadingImg = false
      }
    },

    getUnsplashImageUrl(unsplashPhotoObj) {
      // AHA-8204 - Xử lí load ảnh Unsplash bị chậm.
      // Nguyên nhân: do dùng ảnh RAW, nó nặng, nên thư viện Cropper nó load ảnh chậm.
      // Giải pháp: dùng ảnh optimized, cho phép width tối đa là 1920px
      if (unsplashPhotoObj.width > 1920) {
        return `${unsplashPhotoObj.urls.full}&w=1920`
      }
      return unsplashPhotoObj.urls.full
    },

    getGiphyImageUrl(item) {
      const mediaFormat = this.giphyType === 'sticker' ? 'gif_transparent' : 'gif'

      return item.media_formats[mediaFormat].url
    },

    getGifPreviewUrl(item, type = 'gif') {
      const mediaFormat = type === 'sticker' ? 'webp_transparent' : 'webp'

      return item.media_formats[mediaFormat].url
    },

    giphyInfinite() {
      this.noMoreUnsplashResult = false
      this.searchPhotoGiphy()
    },
    _updateShowQuestionImage() {
      const { slideActive } = this.$store.state
      if (slideActive.type !== 'multipleChoice' && slideActive.type !== 'imageChoice' && slideActive.type !== 'pickAnswer') {
        slideActive.showQuestionImage = true
        return
      }
      const payload = {
        showQuestionImage: !slideActive.multipleCountAnswer,
      }
      this._setSlideActive(payload)
      this._updateSlide(payload)
    },
    _setSlideActive(payload) {
      this.updateSlideActiveAction({
        showQuestionImage: payload.showQuestionImage,
      })
    },
    async _updateSlide(payload) {
      try {
        await SlideApi.updateSlideById(this.slideActive.id, payload)
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Update slide failed. Please try again.'))
      }
    },
    handleOKButtonOnModalUploadImageError() {
      this.hasErrorOnUploadingGiphy = false
    },
    handleGiphyTypeChange(type) {
      if (type !== this.giphyType) {
        this.noResultText = ''
        this.giphyPhotos = []
        this.giphyType = type
        this.gifNextPos = null
        this.focusGifSearchInput()
        this.searchPhotoGiphy()
      }
      if (this.giphyType === 'gif') {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Giphy > Click GIFs button',
        )
      }
      if (this.giphyType === 'sticker') {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_EDITOR,
          'Upload image modal > Giphy > Click Stickers button',
        )
      }
    },
    focusGifSearchInput() {
      setTimeout(() => {
        if (!this.$refs['giphy-search-input']) return

        this.$refs['giphy-search-input'].focus()
      })
    },
    updateText(value) {
      this.text = value
    },
    selectUnsplashImage(type, item) {
      return this.selectImage(this.getUnsplashImageUrl(item), type, item)
    },
    handleImageLoad() {
      this.incrementProcessedGifImageCount()
    },
    handleImageError(event) {
      const img = event.target
      const currentRetryCount = img.getAttribute('data-retry-count') || 0
      const maxRetryCount = 3

      if (currentRetryCount < maxRetryCount) {
        img.setAttribute('data-retry-count', parseInt(currentRetryCount, 10) + 1)
        // eslint-disable-next-line no-self-assign
        img.src = img.src // Retry loading the image
      } else {
        this.gifLoadErrorCount += 1
      }

      this.incrementProcessedGifImageCount()
    },
    incrementProcessedGifImageCount() {
      this.processedGifImageCount += 1

      if (this.processedGifImageCount === this.totalGifImageCount) {
        this.logGifLoadErrors()
      }
    },
    logGifLoadErrors() {
      if (this.gifLoadErrorCount > 0) {
        Sentry.captureMessage(`Tenor > Failed to load ${this.gifLoadErrorCount} images`, {
          level: 'info',
        })

        this.gifLoadErrorCount = 0
      }
    },
  },
}
</script>
